import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type NetworkCallbacks } from '@atlassian/search-client/persist-refresh';
import {
	cachedRecentActivitiesQuery,
	fetchJiraIssuesQuery,
	recentActivitiesQuery,
} from '@atlassian/search-client/recent-activities';
import {
	type ActivityEventType,
	type ActivityItemEdge,
	type ActivityNode,
	type ActivityProduct,
	is1PActivity,
	isAssetsActivity,
	isDocumentActivity,
	isGithubActivity,
	isGraphActivity,
	isLoomActivity,
	isGraphPullRequestActivity,
	isRemoteLinkActivity,
	type JiraIssueById,
	type JiraIssuesQueryVariables,
	ActivityProduct as QuickFindDataProduct,
	type QuickFindGithubEntity,
	type QuickFindRecentActivitiesQueryVariables,
} from '@atlassian/search-client/recent-activities/types';

import {
	PrimaryProductKeys,
	ProductKeys,
	ProductKeys3P,
} from '../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { messages } from '../../../../common/ui/search-result-type/messages';
import { hasAuthorisedSmartLinkConnector } from '../../../../common/utils/connectors';
import { sha256Hash } from '../../../../common/utils/events/sha256-hash';
import {
	serveSlClickedOnlyInRecentsExperiment,
	serve3PClickIngestionExperiment,
	serveGithubRecentsExperiment,
} from '../../../../common/utils/experiments';
import type { RecentActivities } from '../types';

/**
 * Returns whether the Loom recents experiment is enabled.
 * We do NOT trigger the exposure event here since it would be too wide.
 * Instead, we're doing that in src/ui/quick-find/dialog/index.tsx.
 *
 * Only users with Loom access (determined by availableProducts containing Loom)
 * are eligible for the experiment.
 *
 * Jira is intentionally excluded: qf_loom_recents was shipped to non-Jira
 * primaries only via Statsig targeting. Encoding the exclusion here keeps the
 * behaviour correct after experiment cleanup.
 *
 * https://switcheroo.atlassian.com/ui/gates/GATE_ID/key/qf_loom_recents
 */
const isLoomRecentsEnabled = (
	availableProducts?: ProductKeys[],
	primaryProduct?: string,
): boolean => {
	// Jira is excluded from the qf_loom_recents target audience in Statsig.
	// This reflects the decision made when concluding the experiment:
	// https://hello.atlassian.net/wiki/spaces/CentAI/pages/6574370556/Add+Loom+recent+in+QuickFind#Decision
	if (primaryProduct === PrimaryProductKeys.Jira) {
		return false;
	}

	// Only target users who have Loom access
	if (!availableProducts?.includes(ProductKeys.Loom)) {
		return false;
	}

	return (
		FeatureGates.getExperimentValue<string>('qf_loom_recents', 'cohort', 'not-enrolled', {
			fireExperimentExposure: false,
		}) === 'experiment'
	);
};

const hasGithubConnectorAccess = (thirdPartyConfigs?: ThirdPartyConfigsBootstrap): boolean => {
	const githubConfig = thirdPartyConfigs?.[ProductKeys3P.Github];
	return !!(githubConfig && !githubConfig.userNeedsOAuth);
};

export const INCLUDED_PRODUCTS: string[] = ['confluence', 'jira', 'townsquare', 'graph'];

export const INCLUDED_OBJECT_TYPES: string[] = [
	'page',
	'blogpost',
	'whiteboard',
	'issue',
	'project',
	'goal',
	'content',
	'document',
	'remote-link',
];

/**
 * Event types to be included in the recent activities query.
 * Based upon the AVI actions for each product: https://bitbucket.org/atlassian/flight-deck/src/7bab054550927c3c023f2eb783ce9d2ca6279bfc/packages/integrations/worklens/contrib/
 */
const INCLUDED_EVENT_TYPES = [
	'assigned',
	'unassigned',
	'viewed',
	'updated',
	'created',
	'liked',
	'transitioned',
	'published',
	'edited',
	'commented',
	'sl-viewed',
	'sl-clicked',
	'shared',
	'exported',
	'deleted',
	'started',
	'snapshotted',
	'trashed',
	'reaction',
];

const ORS_BATCH_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';
const GRANTED = 'granted';
const RECENT_ACTIVITIES_LIMIT = 200;
const OK_STATUS = 200;

/**
 * Returns the list of products to be included in the recent activities query based on the experiment.
 */
const getIncludedProductsBasedOnCohort = (
	availableProducts?: ProductKeys[],
	primaryProduct?: string,
): string[] => {
	return [
		...INCLUDED_PRODUCTS,
		...(fg('assets_rovo_search_all') ? ['assets'] : []),
		...(isLoomRecentsEnabled(availableProducts, primaryProduct) ? [ProductKeys.Loom] : []),
	];
};

const getIncludedProductsBasedOnProduct = (
	includedProducts: string[],
	product?: string,
): string[] => {
	/**
	 * Decision on Experiment (https://hello.atlassian.net/wiki/spaces/CentAI/pages/5652280226)
	 * Remove Jira from QF Recents to have better relevance and CTR.
	 */
	if (product === PrimaryProductKeys.Confluence) {
		return includedProducts.filter((product) => product !== PrimaryProductKeys.Jira);
	}

	return includedProducts;
};

const getFilteredEventTypes = (thirdPartyConfigs?: ThirdPartyConfigsBootstrap): string[] => {
	// Only consult the sl-clicked-only experiment for users with an authorised
	// smart-link connector — others can't produce `sl-*` recents either way.
	const baseEventTypes =
		hasAuthorisedSmartLinkConnector(thirdPartyConfigs) &&
		serveSlClickedOnlyInRecentsExperiment({ fireExperimentExposure: false })
			? INCLUDED_EVENT_TYPES.filter((eventType) => eventType !== 'sl-viewed')
			: INCLUDED_EVENT_TYPES;

	if (
		thirdPartyConfigs &&
		serve3PClickIngestionExperiment(thirdPartyConfigs, { fireExperimentExposure: false })
	) {
		return [...baseEventTypes, 'clicked'];
	}

	return baseEventTypes;
};

const getFilteredProducts = (
	primaryProduct?: string,
	availableProducts?: ProductKeys[],
): string[] => {
	// Get included products based on cohort
	const includedProductsBasedOnCohort = getIncludedProductsBasedOnCohort(
		availableProducts,
		primaryProduct,
	);

	// Further filter products based on primary product
	const filteredProductsBasedOnProduct = getIncludedProductsBasedOnProduct(
		includedProductsBasedOnCohort,
		primaryProduct,
	);

	return filteredProductsBasedOnProduct;
};

export const fetchRecentActivities: (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	after?: string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	headers?: Record<string, string>,
	primaryProduct?: string,
	networkCallbacks?: NetworkCallbacks,
	availableProducts?: ProductKeys[],
) => Promise<
	{
		id: string;
		title: string;
		data: ActivityItemEdge;
		type: 'recent-activity';
		activityTimestamp: string;
	}[]
> = async (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	after: string = '',
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	headers?: Record<string, string>,
	primaryProduct?: string,
	networkCallbacks?: NetworkCallbacks,
	availableProducts?: ProductKeys[],
) => {
	const filteredProducts = getFilteredProducts(primaryProduct, availableProducts);
	const variables: QuickFindRecentActivitiesQueryVariables = {
		filteredProducts: Array.from(new Set(filteredProducts)),
		filteredObjectTypes: Array.from(
			new Set([
				...INCLUDED_OBJECT_TYPES,
				...(fg('assets_rovo_search_all') ? ['object', 'object_type', 'schema'] : []),
				...(isLoomRecentsEnabled(availableProducts, primaryProduct) ? ['video'] : []),
				...(hasGithubConnectorAccess(thirdPartyConfigs) && serveGithubRecentsExperiment()
					? ['pull-request']
					: []),
			]),
		),
		filteredRootContainerIds: [
			`ari:cloud:platform::site/${cloudId}`,
			`ari:cloud:townsquare::site/${cloudId}`,
			...(isLoomRecentsEnabled(availableProducts, primaryProduct)
				? [`ari:cloud:loom::site/${cloudId}`]
				: []),
		],
		itemsLimit: 200,
		after,
		includeJiraIssueMetadata: true,
		filteredEventTypes: getFilteredEventTypes(thirdPartyConfigs),
	};

	const serveAutorefreshCohort =
		FeatureGates.getExperimentValue<string>(
			'quick_find_autorefresh_experiment_v3',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		) === 'autorefresh';

	const response = serveAutorefreshCohort
		? await cachedRecentActivitiesQuery({
				variables,
				aggAbsoluteUrl,
				headers,
				onlyRefreshIfExpired: true,
				returnExpired: false,
				networkCallbacks,
			})
		: await recentActivitiesQuery({
				variables,
				aggAbsoluteUrl,
				headers,
				networkCallbacks,
			});

	const edges = response.data?.activity?.myActivity?.all?.edges || [];
	return edges.map(mapEssentialData);
};

export const resolveGithubRecentActivities: (
	recentActivities: RecentActivities,
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => Promise<void>,
	headers?: Record<string, string>,
) => Promise<
	{
		id: string;
		title: string;
		data: ActivityItemEdge;
		type: 'recent-activity';
		activityTimestamp: string;
	}[]
> = async (
	recentActivities: RecentActivities,
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => Promise<void>,
	headers?: Record<string, string>,
) => {
	const edges = sortEdgesByTimestamp(recentActivities.result.map((item) => item.data));
	const aris = getJiraAndConfluenceARIs(edges);
	const jiraIssues: JiraIssueById[] = await fetchJiraIssues(
		aris,
		aggAbsoluteUrl,
		logException,
		headers,
	);
	const githubLinkTimestampMap = buildTimestampMapFromJiraIssues(edges, jiraIssues);
	const githubLinks = Array.from(githubLinkTimestampMap.keys());
	const githubResolvedResponse = await objectResolverServiceBatchCall(githubLinks, logException);

	const githubEdges = mapResolvedGithubResponseToRecentActivities(
		githubResolvedResponse,
		githubLinkTimestampMap,
	);
	return githubEdges.map(mapEssentialData);
};

export const jiraIssuesQueryOperationName = 'QuickFindRecentActivitiesJiraIssuesQuery';
export const jiraIssuesQuery: '\n    query QuickFindRecentActivitiesJiraIssuesQuery($jira_ids: [ID!]!) {\n\t\t\tjira_issuesByIds(ids: $jira_ids) {\n\t\t\t\tid,\n\t\t\t\tsummary,\n\t\t\t\tdescriptionField {\n\t\t\t\t\tdescription,\n\t\t\t\t\trichText {\n\t\t\t\t\t\tadfValue {\n\t\t\t\t\t\t\tconvertedPlainText {\n\t\t\t\t\t\t\t\tplainText\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t}\n\t\t\t\t\t}\n\t\t\t\t}\n\t\t\t}\n\t\t}\n  ' = `
    query ${jiraIssuesQueryOperationName}($jira_ids: [ID!]!) {
			jira_issuesByIds(ids: $jira_ids) {
				id,
				summary,
				descriptionField {
					description,
					richText {
						adfValue {
							convertedPlainText {
								plainText
							}
						}
					}
				}
			}
		}
  `;

export class JiraBackendApiError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'JiraBackendApiError';
	}
}

export const fetchJiraIssues: (
	aris: string[],
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => void,
	headers?: Record<string, string>,
) => Promise<JiraIssueById[]> = async (
	aris: string[],
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => void,
	headers?: Record<string, string>,
): Promise<JiraIssueById[]> => {
	const jiraIssueAris = aris.filter((ari) => {
		try {
			const [resourceOwner, , resourceType] = getProductAndIDFromARI(ari);
			return resourceOwner === QuickFindDataProduct.JIRA && resourceType === 'issue';
		} catch {
			return false;
		}
	});

	if (jiraIssueAris.length === 0) {
		return [];
	}

	// From https://bitbucket.org/atlassian/graphql-central-schema/src/d0613f3cd3a46e91b2e91ad919ecc4c5a47140f0/schema/jira/gira/IssueView.nadel#lines-17
	// Only fetch first 100 items for experiment
	const limitedJiraIssueAris = jiraIssueAris.slice(0, 100);

	try {
		const result = await fetchJiraIssuesQuery({
			variables: { jira_ids: limitedJiraIssueAris } satisfies JiraIssuesQueryVariables,
			graphQuery: jiraIssuesQuery,
			aggAbsoluteUrl,
			operationName: jiraIssuesQueryOperationName,
			headers,
		});
		return (result?.data?.jira_issuesByIds as JiraIssueById[]) || [];
	} catch (error) {
		logException(new JiraBackendApiError(`Failed to fetch jira issues: ${error}`));
		return [];
	}
};

export const mapEssentialData: (result: ActivityItemEdge) => {
	id: string;
	title: string;
	data: ActivityItemEdge;
	type: 'recent-activity';
	activityTimestamp: string;
} = (result: ActivityItemEdge) => {
	const product = result.node.object.product;
	let title = '';
	const timestamp = result.node?.event?.timestamp;

	if (is1PActivity(result.node.object)) {
		if (product === 'confluence') {
			title = result.node.object.data?.title || '';
		} else if (product === 'jira') {
			const key = result.node.object.data?.key;
			const summary = result.node.object.data?.fieldsById?.edges?.[0]?.node?.text;
			title = key && summary ? `${key}: ${summary}` : '';
		} else if (product === 'townsquare') {
			title = result.node.object.data?.name || '';
		}
	} else if (
		(isGraphActivity(result.node.object) &&
			(isDocumentActivity(result.node.object) ||
				isRemoteLinkActivity(result.node.object) ||
				isGraphPullRequestActivity(result.node.object))) ||
		isGithubActivity(result.node.object)
	) {
		title = result.node.object.data?.displayName || '';
	} else if (isLoomActivity(result.node.object)) {
		title = result.node.object.data?.name || '';
	} else if (isAssetsActivity(result.node.object)) {
		title = result.node.object.data?.assetsLabel || '';
	} else {
		title = '';
	}

	return {
		id: result.node.object.id,
		title,
		data: result,
		type: 'recent-activity' as const,
		activityTimestamp: timestamp,
	};
};

export const getJiraAndConfluenceARIs: (edges: ActivityItemEdge[]) => string[] = (
	edges: ActivityItemEdge[],
): string[] => {
	let aris: string[] = [];
	edges.forEach((item) => {
		const ari = item.node.object.id;
		if (ari && (isConfluenceResult(ari) || isJiraResult(ari))) {
			aris.push(ari);
		}
	});
	return aris;
};

export const buildTimestampMapFromJiraIssues: (
	edges: ActivityItemEdge[],
	jiraIssuesById: JiraIssueById[],
) => Map<string, string> = (
	edges: ActivityItemEdge[],
	jiraIssuesById: JiraIssueById[],
): Map<string, string> => {
	const githubLinkTimestampMap = new Map<string, string>();

	jiraIssuesById.forEach((jiraIssue: JiraIssueById) => {
		if (jiraIssue?.id) {
			const correspondingEdge = edges.find((edge) => edge.node.object.id === jiraIssue.id);
			if (correspondingEdge) {
				const timestamp = correspondingEdge.node.event.timestamp;

				const githubLinks = scrapeGithubLinksFromSingleDocument(jiraIssue);
				githubLinks.forEach((link) => {
					githubLinkTimestampMap.set(link, timestamp);
				});
			}
		}
	});
	return githubLinkTimestampMap;
};

export const scrapeGithubLinksFromSingleDocument: (jiraIssue: JiraIssueById) => string[] = (
	jiraIssue: JiraIssueById,
): string[] => {
	const githubLinks: Set<string> = new Set();
	const githubRegex =
		/https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+(?:\/(?:issues\/\d+|pull\/\d+|commit\/[a-f0-9]+|blob\/[\w\-\.\/]+|tree\/[\w\-\.\/]+))?(?=\s|$|[^\w\-\/\.])/gi;

	const scanText = (text: string | undefined) => {
		if (!text) {
			return;
		}

		const matches = text.match(githubRegex);
		if (matches) {
			matches.forEach((link) => githubLinks.add(link));
		}
	};

	scanText(jiraIssue.summary);
	scanText(jiraIssue.descriptionField?.description);
	scanText(jiraIssue.descriptionField?.richText?.adfValue?.convertedPlainText?.plainText);

	return Array.from(githubLinks);
};

export class OrsApiError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'OrsApiError';
	}
}

export const objectResolverServiceBatchCall: (
	githubLinks: string[],
	logException: (error: Error) => void,
) => Promise<any[]> = async (
	githubLinks: string[],
	logException: (error: Error) => void,
): Promise<any[]> => {
	if (githubLinks.length === 0) {
		return [];
	}

	// https://bitbucket.org/atlassian/object-resolver-service/src/178ab83b4d54575d9ff2cd1246eb83a892b4fd6a/openapi.yaml#lines-335
	// Batch requests in chunks of 50 for experiment, with a maximum of 2 batches
	const BATCH_SIZE = 50;
	const MAX_BATCHES = 2;
	const allResults: any[] = [];

	const maxLinks = BATCH_SIZE * MAX_BATCHES;
	const linksToProcess = githubLinks.slice(0, maxLinks);

	for (let i = 0; i < linksToProcess.length; i += BATCH_SIZE) {
		const batch = linksToProcess.slice(i, i + BATCH_SIZE);
		const orsRequestData = batch.map((link: string) => ({
			resourceUrl: link,
		}));

		try {
			const response = await fetch(ORS_BATCH_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-product': 'JiraQuickFindRecentsForGithub', // For tracking RPM in ORS API
				},
				body: JSON.stringify(orsRequestData),
			});

			if (response === null || !response.ok) {
				logException(
					new OrsApiError(`ORS API call failed: ${response.status} ${response.statusText}`),
				);
			} else {
				const batchResults = await response.json();
				allResults.push(...batchResults);
			}
		} catch (error) {
			logException(new OrsApiError(`ORS API call error: ${error}`));
		}
	}

	return allResults;
};

export const mapResolvedGithubResponseToRecentActivities: (
	githubResolvedResponse: any[],
	githubLinkTimestampMap: Map<string, string>,
) => ActivityItemEdge[] = (
	githubResolvedResponse: any[],
	githubLinkTimestampMap: Map<string, string>,
) => {
	const edges: ActivityItemEdge[] = [];
	githubResolvedResponse.forEach((resolvedResponse: any) => {
		if (
			resolvedResponse.status === OK_STATUS &&
			resolvedResponse.body.meta.access === GRANTED &&
			edges.length < RECENT_ACTIVITIES_LIMIT
		) {
			const originalTimestamp = githubLinkTimestampMap.get(resolvedResponse.body.data.url);
			const currentTimestamp = new Date().toISOString();
			const kebabCaseResourceType = camelToKebab(resolvedResponse.body.meta.resourceType);
			const typeMessage =
				kebabCaseResourceType && kebabCaseResourceType in messages
					? messages[kebabCaseResourceType as keyof typeof messages]
					: messages.unrecognised;

			const githubEdge: ActivityItemEdge = {
				node: {
					object: {
						data: {
							displayName: resolvedResponse.body.data.name || resolvedResponse.body.data.title,
							url: resolvedResponse.body.data.url,
						},
						id: sha256Hash(resolvedResponse.body.data['@id']), // Must hash id as it's ugc
						type: typeMessage.defaultMessage,
						product: 'github' as ActivityProduct,
					},
					event: {
						eventType: 'viewed' as ActivityEventType,
						timestamp: originalTimestamp || currentTimestamp,
					},
				} as ActivityNode<QuickFindGithubEntity>,
				cursor: '',
			};
			edges.push(githubEdge);
		}
	});

	return limitToMostRecentActivities(sortEdgesByTimestamp(edges), RECENT_ACTIVITIES_LIMIT);
};

const sortEdgesByTimestamp = (edges: ActivityItemEdge[]) => {
	return edges.sort((a, b) => {
		const timeA = new Date(a.node.event.timestamp).getTime();
		const timeB = new Date(b.node.event.timestamp).getTime();
		return timeB - timeA; // Descending order
	});
};

const limitToMostRecentActivities = (
	edges: ActivityItemEdge[],
	limit: number = 200,
): ActivityItemEdge[] => {
	return edges.slice(0, limit);
};

const isJiraResult = (ari: string) => {
	return ari.includes(':jira:');
};

const isConfluenceResult = (ari: string) => {
	return ari.includes(':confluence:');
};

/**
 * Converts camelCase string to kebab-case
 * e.g., 'pullRequest' -> 'pull-request'
 */
const camelToKebab = (str: string): string => {
	return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};
